/**
 * Browser Notification Service for FinAssure
 * Handles permission requests and notification dispatching
 */

class NotificationService {
    constructor() {
        this.permissionGranted = false;
        this.checkPermission();
    }

    /**
     * Check current notification permission status
     */
    checkPermission() {
        if (!('Notification' in window)) {
            console.log('This browser does not support notifications');
            return false;
        }

        this.permissionGranted = Notification.permission === 'granted';
        return this.permissionGranted;
    }

    /**
     * Request notification permission from user
     */
    async requestPermission() {
        if (!('Notification' in window)) {
            return false;
        }

        if (Notification.permission === 'granted') {
            this.permissionGranted = true;
            return true;
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            this.permissionGranted = permission === 'granted';
            return this.permissionGranted;
        }

        return false;
    }

    /**
     * Send a notification for a reminder
     * @param {Object} reminder - Reminder object with title, amount, due_date
     */
    sendReminderNotification(reminder) {
        if (!this.permissionGranted) {
            console.log('Notification permission not granted');
            return;
        }

        const title = `💰 Payment Reminder: ${reminder.title}`;
        const options = {
            body: `Amount: ₹${reminder.amount}\nDue: ${reminder.due_date}\nDon't forget to pay!`,
            icon: '/logo192.png', // Your app logo
            badge: '/logo192.png',
            tag: `reminder-${reminder.id}`,
            requireInteraction: true, // Notification stays until user interacts
            vibrate: [200, 100, 200], // Vibration pattern
            data: {
                reminderId: reminder.id,
                url: '/reminders'
            }
        };

        const notification = new Notification(title, options);

        // Handle notification click - navigate to reminders page
        notification.onclick = (event) => {
            event.preventDefault();
            window.focus();
            window.location.href = '/reminders';
            notification.close();
        };

        return notification;
    }

    /**
     * Check for overdue or upcoming reminders and notify
     * @param {Array} reminders - Array of reminder objects
     */
    checkAndNotify(reminders) {
        if (!this.permissionGranted) return;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const threeDaysFromNow = new Date(today);
        threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

        reminders.forEach(reminder => {
            if (reminder.is_completed) return; // Skip completed

            const dueDate = new Date(reminder.due_date);
            dueDate.setHours(0, 0, 0, 0);

            // Notify if overdue or due within 3 days
            if (dueDate <= threeDaysFromNow) {
                const hasBeenNotified = localStorage.getItem(`notified-${reminder.id}`);

                // Only notify once per day per reminder
                const lastNotifiedDate = hasBeenNotified ? new Date(hasBeenNotified) : null;
                const todayStr = today.toDateString();

                if (!lastNotifiedDate || lastNotifiedDate.toDateString() !== todayStr) {
                    this.sendReminderNotification(reminder);
                    localStorage.setItem(`notified-${reminder.id}`, today.toISOString());
                }
            }
        });
    }

    /**
     * Schedule daily notification check
     */
    scheduleDailyCheck(reminders) {
        // Check immediately
        this.checkAndNotify(reminders);

        // Then check every hour
        setInterval(() => {
            this.checkAndNotify(reminders);
        }, 60 * 60 * 1000); // Every hour
    }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
